import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import Typography from '../Typography';
import HelpText from './HelpText';
import InputErrors from './InputErrors';

const useStyles = createUseStyles((theme) => ({
  input: {
    border: `1px solid ${theme.borderColor.default}`,
    borderRadius: theme.borderRadius.sm,
    color: theme.palette.black,
    display: 'block',
    fontSize: theme.textSizes.base,
    padding: '0.75rem',
    resize: 'vertical',
    transition: theme.transitions.border,
    width: '100%',
    '&:focus': {
      borderColor: theme.palette.info[400],
      boxShadow: theme.shadows.input,
      outline: 0,
    },
    '&:active': {
      borderColor: theme.palette.neutral[350],
    },
  },
  error: {
    borderColor: theme.palette.danger[400],
  },
  label: {
    cursor: 'pointer',
    marginBottom: '0.5rem',
  },
}));

const TextareaInput = ({
  ariaRequired,
  errors,
  helpText,
  id,
  label,
  mask,
  name,
  onBlur,
  value,
}) => {
  const classes = useStyles({ theme: useTheme() });

  const [val, setVal] = useState(null);

  const handleChange = (event) => {
    setVal(event.target.value);
  };

  return (
    <>
      <label htmlFor={id || name} {...(mask && { 'data-personal': true })}>
        <div className={classes.label}>
          <Typography variant="label">{label}</Typography>
        </div>
        <textarea
          aria-required={ariaRequired}
          className={classNames(
            classes.input,
            errors.length > 0 && classes.error,
          )}
          id={id || name}
          name={name}
          onBlur={onBlur}
          onChange={handleChange}
          rows="6"
          value={val === null ? value : val}
          {...(errors.length > 0
            ? { 'aria-invalid': true, 'aria-describedby': `error-${name}` }
            : {})}
        />
      </label>
      {errors.length > 0 ? (
        <InputErrors errors={errors} id={`error-${name}`} />
      ) : (
        <HelpText value={helpText} />
      )}
    </>
  );
};

TextareaInput.propTypes = {
  ariaRequired: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  helpText: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
  mask: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

TextareaInput.defaultProps = {
  ariaRequired: false,
  errors: [],
  helpText: null,
  id: '',
  mask: false,
};

export default TextareaInput;
